import type { CampaignStatus, PlatformProductStatus } from '~/types/graphql';
import type { ICampaignType, IVariantType } from '~/modules/campaign/types';
import type { TemplateType } from '~/types/template';

export interface ITemplateUsageDetail {
  campaignId: string;
  campaignStatus?: CampaignStatus;
  campaignName?: string;
  /** Type of the campaign holding the usage. Only set when it is not the type the modal is editing. */
  campaignType?: ICampaignType;
  isOtherGroupCampaign?: boolean;
  isOtherSingerCampaign?: boolean;
  isOtherPriceCampaign?: boolean;
  /** A URL test holds a template through the page its variant sends traffic to. */
  isOtherUrlCampaign?: boolean;
  currentCampaignGroupType?: IVariantType;
  currentCampaignType?: IVariantType;
  /** The variant's price record id. Only set for a price test, which locks a template through its product. */
  priceId?: string;
  /** The variant's URL destination record id. Only set for a URL test. */
  urlId?: string;
  /**
   * The record the test holds the template through — the product a price test runs on, or the page a
   * URL test sends traffic to. Only set for those two, which pin a template rather than name one.
   */
  resourceName?: string;
  /** Shopify status of that product — Active vs. Unlisted, shown by the Edit experiments row. Price tests only. */
  productStatus?: PlatformProductStatus;
}

/** Template usages keyed by the template suffix they occupy. Several campaigns can share a suffix. */
export type IUsedTemplateSuffixes = Record<string, ITemplateUsageDetail[]>;

interface IBaseDisableReason {
  isHasShortcut?: boolean;
}
export enum IDisableReasonKey {
  IS_USED_FOR_OTHER_CAMPAIGN_GROUP = 'IS_USED_FOR_OTHER_CAMPAIGN_GROUP',
  IS_USED_FOR_OTHER_CAMPAIGN = 'IS_USED_FOR_OTHER_CAMPAIGN',
  IS_USED_FOR_CONTROL = 'IS_USED_FOR_CONTROL',
  IS_USED_FOR_VARIANT = 'IS_USED_FOR_VARIANT',
  IS_SELECTED = 'IS_SELECTED',
  IS_NO_ASSIGNED_BY_GEMPAGES = 'IS_NO_ASSIGNED_BY_GEMPAGES',
  IS_NO_ASSIGNED_BY_SHOPIFY = 'IS_NO_ASSIGNED_BY_SHOPIFY',
  IS_TEMPLATE_DRAFT = 'IS_TEMPLATE_DRAFT',
  IS_TEMPLATE_USED_TO_TEST = 'IS_TEMPLATE_USED_TO_TEST',
  IS_TEMPLATE_VARIANT_NOT_ASSIGNED = 'IS_TEMPLATE_VARIANT_NOT_ASSIGNED',
}
// Interfaces for each disable reason with specific keys and properties
interface IUsedForControlDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_USED_FOR_CONTROL;
  content: () => React.ReactNode | string;
}

interface IUsedForVariantDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_USED_FOR_VARIANT;
  content: () => React.ReactNode | string;
}

interface ISelectedDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_SELECTED;
  content: () => React.ReactNode | string;
}

interface INoAssignedByGemPagesDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_NO_ASSIGNED_BY_GEMPAGES;
  hidePageAssigned: boolean;
  content: (props: { templateType: TemplateType }) => string;
}

interface INoAssignedByShopifyDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_NO_ASSIGNED_BY_SHOPIFY;
  hidePageAssigned: boolean;
  content: (props: { templateType: TemplateType }) => string;
}

interface ITemplateDraftDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_TEMPLATE_DRAFT;
  content: () => React.ReactNode | string;
}

interface ITemplateUsedToTestDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_TEMPLATE_USED_TO_TEST;
  content: (props: {
    assignedTotal: number;
    assignedCount: number;
    templateType: string;
  }) => string;
}

type TCampaignDisableProps = { campaignStatus?: CampaignStatus; campaignName?: string };

interface IUsedForOtherCampaignDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN;
  content: (props: TCampaignDisableProps) => React.ReactNode | string;
}

interface IUsedForOtherCampaignGroupDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_USED_FOR_OTHER_CAMPAIGN_GROUP;
  content: (props: TCampaignDisableProps) => React.ReactNode | string;
}

interface ITemplateVariantNotAssignedDisable extends IBaseDisableReason {
  key: IDisableReasonKey.IS_TEMPLATE_VARIANT_NOT_ASSIGNED;
  content: () => React.ReactNode | string;
}

// Union type for all disable reasons
export type IDisableReasonEntry =
  | IUsedForControlDisable
  | IUsedForVariantDisable
  | ISelectedDisable
  | INoAssignedByGemPagesDisable
  | INoAssignedByShopifyDisable
  | ITemplateDraftDisable
  | IUsedForOtherCampaignDisable
  | IUsedForOtherCampaignGroupDisable
  | ITemplateUsedToTestDisable
  | ITemplateVariantNotAssignedDisable;
